<script setup lang="ts">
import { computed, ref } from 'vue';
import type { EntityDocument } from '../types/entities';
const props = defineProps<{ entity: EntityDocument; readonly: boolean; flush: () => Promise<void>; changeStatus: (draft: boolean, entityId: string) => Promise<unknown> }>();
const emit = defineEmits<{ openEntity: [id: string] }>();
const busy = ref(false);
const error = ref('');
const isChapter = computed(() => props.entity.templateCategory === 'kapitel');
const mainLabel = computed(() => isChapter.value ? 'Hauptkapitel' : 'Hauptszene');
async function change() {
  const entityId = props.entity.id;
  const makeDraft = !props.entity.isStoryDraft;
  const message = makeDraft
    ? `${props.entity.title} zum Entwurf machen? ${isChapter.value ? 'Das Kapitel und seine Szenen' : 'Diese Szene'} werden aus Timeline und Graphen ausgeblendet. Texte, Verknüpfungen und Versionshistorie bleiben erhalten.`
    : `${props.entity.title} als ${mainLabel.value} festlegen? ${isChapter.value ? 'Das Kapitel und seine Hauptszenen erscheinen' : 'Die Szene erscheint'} wieder in Timeline und Graphen.`;
  if (!window.confirm(message)) return;
  busy.value = true; error.value = '';
  try { await props.flush(); await props.changeStatus(makeDraft, entityId); }
  catch (reason) { error.value = reason instanceof Error ? reason.message : 'Der Status konnte nicht geändert werden.'; }
  finally { busy.value = false; }
}
</script>

<template>
  <section class="writing-status-control" aria-label="Kapitel- und Szenenstatus">
    <div class="writing-status-row"><strong>{{ entity.isStoryDraft ? 'Entwurf' : mainLabel }}</strong>
      <button v-if="entity.draftChapterId" type="button" @click="emit('openEntity', entity.draftChapterId)">Entwurfsstatus im Kapitel ändern</button>
      <button v-else-if="!readonly" type="button" :disabled="busy" @click="change">{{ busy ? 'Status wird geändert …' : entity.isStoryDraft ? `Als ${mainLabel} festlegen` : 'Zum Entwurf machen' }}</button>
    </div>
    <p v-if="entity.isStoryDraft">{{ entity.draftChapterId ? 'Das zugehörige Kapitel ist ein Entwurf.' : isChapter ? 'Dieses Kapitel ist ein Entwurf; seine Szenen folgen diesem Status.' : 'Diese Szene ist ein Entwurf.' }} Entwürfe bleiben bearbeitbar und sind in Timeline und Graphen ausgeblendet.</p>
    <p v-if="error" class="writing-status-error" role="alert">{{ error }}</p>
  </section>
</template>

<style scoped>
.writing-status-control { padding: 10px 22px; border-bottom: 1px solid var(--ui-border); background: var(--ui-surface-2); color: var(--ui-text); }
.writing-status-row { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; }
button { padding: 7px 12px; border: 1px solid var(--ui-accent); border-radius: var(--ui-radius-md); color: var(--ui-text); background: var(--ui-accent-soft); font: inherit; cursor: pointer; }
button:disabled { opacity: .5; cursor: default; } p { margin: 8px 0 0; color: var(--ui-text-muted); } .writing-status-error { color: var(--ui-danger-text); }
</style>
